import { useMemo, useCallback } from 'react';
import { Stack, Button, TableRow, TableCell, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { IOrderDocument, ORDER_DOCUMENT_TYPES } from '@/types/order-document';
import { useBoolean } from '@/hooks/state/use-boolean';
import { ShowIf } from '@/utils/ui/showIf';
import { getExtension, getIconFromExtension } from '@/utils/helpers/common';
import { usePopover } from '@/components/overlays/custom-popover';
import { ConfirmDialog } from '@/components/overlays/custom-dialog';
import { getDocumentTypeChip } from '../../utils/order-document.utils';

import PDFViewer from '@/components/media/preview-documents/preview-documents';
import DownloadButton from './salesOrder-document-download';
import moment from 'moment';

type Props = {
  row: IOrderDocument;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
  hideEditButton?: boolean;
  hideDeleteButton?: boolean;
};

export function OrderDocumentTableRow({
  row,
  onEditRow,
  onDeleteRow,
  hideEditButton = false,
  hideDeleteButton = false,
}: Props) {
  const confirm = useBoolean();
  const { t } = useTranslation();
  const popover = usePopover();

  const documentTypeDisplay = useMemo(
    () => getDocumentTypeChip(row.documentType, t),
    [row.documentType, t]
  );

  const dateDisplay = useMemo(() => {
    if (row.documentDate) {
      return <Typography variant="body2">{moment(row.documentDate).utc().format('L')}</Typography>;
    }
    return (
      <Typography variant="body2" color="text.secondary">
        {t('No date')}
      </Typography>
    );
  }, [row.documentDate, t]);

  const fileDisplay = useMemo(() => {
    if (row?.file?.url) {
      const extension = getExtension(row.file.name || row.file.url);
      const iconType = getIconFromExtension(extension);

      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <img
            alt={row.file.name || 'Document'}
            src={`/assets/icons/files/ic_${iconType}.svg`}
            style={{ height: 20, width: 20 }}
          />
          <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
            {row.file.name}
          </Typography>
        </Stack>
      );
    }
    return (
      <Typography variant="caption" color="text.secondary">
        {t('No file')}
      </Typography>
    );
  }, [row?.file?.url, row?.file?.name, t]);

  // Memoized handlers
  const handleConfirmDelete = useCallback(() => {
    confirm.onTrue();
    popover.onClose();
  }, [confirm, popover]);

  const handleEditClick = useCallback(() => {
    onEditRow();
    popover.onClose();
  }, [onEditRow, popover]);

  return (
    <>
      <TableRow hover>
        <TableCell>{documentTypeDisplay}</TableCell>

        <TableCell align="center">{fileDisplay}</TableCell>

        <TableCell>{dateDisplay}</TableCell>

        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            {/* Download Button */}
            <ShowIf condition={Boolean(row?.file?.url)}>
              <DownloadButton
                fileUrl={row.file?.url || ''}
                fileName={row.file?.name || `document.${getExtension(row.file?.url || '')}`}
                icon={'material-symbols:download'}
                color="default"
              />
            </ShowIf>

            {/* Preview Button */}
            <ShowIf condition={Boolean(row?.file?.url)}>
              <PDFViewer
                file={row.file?.url || ''}
                fileName={row.file?.name || ''}
                fileSize={row.file?.size || 0}
              />
            </ShowIf>

            {/* Edit Button */}
            <ShowIf condition={!hideEditButton}>
              <IconButton size="small" onClick={handleEditClick} title={t('Edit')}>
                <EditIcon fontSize="small" />
              </IconButton>
            </ShowIf>

            {/* Delete Button */}
            <ShowIf condition={!hideDeleteButton}>
              <IconButton
                size="small"
                onClick={handleConfirmDelete}
                title={t('Delete')}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ShowIf>
          </Stack>
        </TableCell>
      </TableRow>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('Delete Document')}
        content={t('Are you sure you want to delete this document? This action cannot be undone.')}
        action={
          <Button variant="contained" color="error" onClick={onDeleteRow}>
            {t('Delete')}
          </Button>
        }
      />
    </>
  );
}
